import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ActivePeriodsField, periodAttributes, periodRows, type ActivePeriod } from "@/components/ActivePeriodsField"

type BandFormProps = {
  id: number | null
  slug?: string
  name: string
  content: string | null
  meta_title: string | null
  meta_description: string | null
  meta_keywords: string | null
  all_recordings_added: boolean
  in_amh: boolean
  amh_url: string | null
  active_periods: ActivePeriod[]
  home_city: string | null
  home_state: string | null
}

const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"]

const inputClass = "w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"

type PageProps = {
  band: BandFormProps
  errors?: Record<string, string>
}

export default function BandForm({ band }: { band: BandFormProps }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = band.id === null
  const [form, setForm] = useState({
    name:                 band.name ?? "",
    content:              band.content ?? "",
    meta_title:           band.meta_title ?? "",
    meta_description:     band.meta_description ?? "",
    meta_keywords:        band.meta_keywords ?? "",
    all_recordings_added: band.all_recordings_added ?? false,
    in_amh:               band.in_amh ?? false,
    amh_url:              band.amh_url ?? "",
    home_city:            band.home_city ?? "",
    home_state:           band.home_state ?? "",
  })

  const [periods, setPeriods] = useState(periodRows(band.active_periods))

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const target = e.target as HTMLInputElement
    setForm((prev) => ({
      ...prev,
      [target.name]: target.type === "checkbox" ? target.checked : target.value,
    }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isNew) {
      router.post("/bands", { band: { ...form, active_periods_attributes: periodAttributes(periods) } })
    } else {
      router.patch(`/bands/${band.slug}`, { band: { ...form, active_periods_attributes: periodAttributes(periods) } })
    }
  }

  const title = isNew ? "New band" : `Edit ${band.name}`
  const cancelHref = isNew ? "/bands" : `/bands/${band.slug}`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`${title} — Australian Music Database.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`${title} — Australian Music Database.`} />
      </Head>

      <main className="mx-auto max-w-2xl px-4 py-8">
        <div className="border-b border-hairline pb-6">
          <h1>{title}</h1>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm text-ink-muted mb-1">Name</label>
            <input
              id="name" name="name" type="text"
              value={form.name} onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="content" className="block text-sm text-ink-muted mb-1">Bio</label>
            <textarea
              id="content" name="content" rows={8}
              value={form.content} onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
          </div>

          <ActivePeriodsField
            legend="Years active"
            hint="Add a period for each run: formed to split, then any reformation. Leave the end blank if it's still going or not known."
            rows={periods} onChange={setPeriods} errors={errors} inputClassName={inputClass}
          />

          <div className="grid grid-cols-[1fr_8rem] gap-2">
            <div>
              <label htmlFor="home_city" className="block text-sm text-ink-muted mb-1">Home town</label>
              <input
                id="home_city" name="home_city" type="text" placeholder="e.g. Brisbane"
                value={form.home_city} onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="home_state" className="block text-sm text-ink-muted mb-1">State</label>
              <select
                id="home_state" name="home_state"
                value={form.home_state} onChange={handleChange}
                className={inputClass}
              >
                <option value="">—</option>
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {errors.home_state && <p className="col-span-2 text-xs text-red-600">{errors.home_state}</p>}
          </div>

          <div>
            <label htmlFor="meta_title" className="block text-sm text-ink-muted mb-1">Meta title</label>
            <input
              id="meta_title" name="meta_title" type="text"
              value={form.meta_title} onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
          </div>

          <div>
            <label htmlFor="meta_description" className="block text-sm text-ink-muted mb-1">Meta description</label>
            <input
              id="meta_description" name="meta_description" type="text"
              value={form.meta_description} onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
          </div>

          <div>
            <label htmlFor="meta_keywords" className="block text-sm text-ink-muted mb-1">Meta keywords</label>
            <input
              id="meta_keywords" name="meta_keywords" type="text"
              value={form.meta_keywords} onChange={handleChange}
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="all_recordings_added" name="all_recordings_added" type="checkbox"
              checked={form.all_recordings_added} onChange={handleChange}
              className="h-4 w-4"
            />
            <label htmlFor="all_recordings_added" className="text-sm text-ink-body">All recordings added</label>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="in_amh" name="in_amh" type="checkbox"
              checked={form.in_amh} onChange={handleChange}
              className="h-4 w-4"
            />
            <label htmlFor="in_amh" className="text-sm text-ink-body">In AMH</label>
          </div>

          <div>
            <label htmlFor="amh_url" className="block text-sm text-ink-muted mb-1">AMH URL</label>
            <input
              id="amh_url" name="amh_url" type="url"
              value={form.amh_url} onChange={handleChange}
              placeholder="https://australianmusichistory.com/…"
              className="w-full rounded border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-1 focus:ring-current"
            />
            <p className="mt-1 text-xs text-ink-muted">The record's post on australianmusichistory.com, if it has one. Setting it ticks In AMH.</p>
            {errors.amh_url && <p className="mt-1 text-xs text-red-600">{errors.amh_url}</p>}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create band" : "Update band"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </>
  )
}
